import _ from 'lodash'
import { ExecutableCalc, executableCalcKey, ExecutableCalcPrecedence } from './executable/ExecutableTypes'
import { ConceptsByCid } from '../concept/Concept'
import { err, ok, Result } from 'neverthrow'
import { createSelector } from 'reselect'

export const CalcRegistryErrorMsgs = {
	AlreadyRegistered: (calc: ExecutableCalc) => `${_.capitalize(calc.calcType)} calc for '${calc.outputCid}' already registered`,
	OutputNotFound: (calc: ExecutableCalc) => `For ${calc.calcType} calc requested output concept '${calc.outputCid}' not found`,
	InputNotFound: (calc: ExecutableCalc, inputCid: string) => `For ${calc.calcType} calc '${calc.outputCid}' requested input concept '${inputCid}' not found`
} as const

export class CalcRegistry {
	concepts: ConceptsByCid
	private calcKeyToCalc: Record<string, ExecutableCalc> = {}

	constructor(concepts?: ConceptsByCid) {
		this.concepts = concepts ?? {}
	}

	register(calcs: ExecutableCalc[]): Result<void, string[]> {
		const errs: string[] = []
		for(let calc of calcs) {
			let calcErrs: string[] = []
			const { calcKey, outputCid, inputCids } = calc
			if(this.calcKeyToCalc[calcKey]) {
				calcErrs.push(CalcRegistryErrorMsgs.AlreadyRegistered(calc))
			}
			if(!this.concepts[outputCid]) {
				calcErrs.push(CalcRegistryErrorMsgs.OutputNotFound(calc))
			}
			for(let inputCid of inputCids) {
				if(!this.concepts[inputCid]) {
					calcErrs.push(CalcRegistryErrorMsgs.InputNotFound(calc, inputCid))
				}
			}
			if(calcErrs.length > 0) {
				errs.push(...calcErrs)
			} else {
				this.calcKeyToCalc[calcKey] = calc
			}
		}
		// rewrite the object so that the standard === comparison on the
		// selector below will cause the selector to invalidate;
		// i figure better to get this out of the way up front on
		// registration than to run a shallowEqual on each redux dispatch
		this.calcKeyToCalc = { ...this.calcKeyToCalc }
		if(errs.length > 0) {
			return err(errs)
		}
		return ok()
	}

	// return all calcs
	// including defaults that shouldn't be executed
	getAllCalcs(): ExecutableCalc[] {
		return Object.values(this.calcKeyToCalc)
	}

	// disregard default calculations when there is a matching primary
	private selectActiveCalcs = createSelector(
		(calcKeyToCalc: Record<string, ExecutableCalc>) => calcKeyToCalc,
		(calcKeyToCalc): Record<string, ExecutableCalc> => {
			return Object.fromEntries(
			Object.entries(calcKeyToCalc).filter(([_calcKey, calc]) => {
				const { precedence, outputCid, calcType } = calc
				return !Boolean(
					precedence === ExecutableCalcPrecedence.Default &&
					calcKeyToCalc[executableCalcKey(outputCid, calcType, ExecutableCalcPrecedence.Primary)]
				)
			})
		)}
	)

	// return all calcs that are not overriden defaults
	getActiveCalcs(): ExecutableCalc[] {
		return Object.values(this.selectActiveCalcs(this.calcKeyToCalc))
	}

	private selectInputCidToCalcKeys = createSelector(
		this.selectActiveCalcs,
		(calcKeyToCalc): Record<string, string[]> => {
			const inputCidToCalcKeys: Record<string, string[]> = {}
			for(let [calcKey, calc] of Object.entries(calcKeyToCalc)) {
				for(let inputCid of calc.inputCids) {
					if(!inputCidToCalcKeys[inputCid]) {
						inputCidToCalcKeys[inputCid] = []
					}
					inputCidToCalcKeys[inputCid].push(calcKey)
				}
			}
			return inputCidToCalcKeys
		},
	)
	
	private get inputCidToCalcKeys(): Record<string, string[]> {
		return this.selectInputCidToCalcKeys(this.calcKeyToCalc)
	}

	getAffectedCalcs(changedInputCids: Set<string>): ExecutableCalc[] {
		const calcKeys = new Set<string>()
		for(let inputCid of changedInputCids) {
			if(this.inputCidToCalcKeys[inputCid]) {
				for(let calcKey of this.inputCidToCalcKeys[inputCid]) {
					calcKeys.add(calcKey)
				}
			}
		}
		const calcs: ExecutableCalc[] = []
		for(let calcKey of calcKeys) {
			if(this.calcKeyToCalc[calcKey]) {
				calcs.push(this.calcKeyToCalc[calcKey])
			}
		}
		return calcs
	}

	// raw selector output for automated testing of memoization
	testSelectActiveCalcsOut() {
		return this.selectActiveCalcs(this.calcKeyToCalc)
	}
	
	// raw selector output for automated testing of memoization
	testSelectInputCidToCalcKeys() {
		return this.selectInputCidToCalcKeys(this.calcKeyToCalc)
	}
}
